import React from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from 'react-leaflet';
import _ from 'lodash';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Entity } from '@sketchpixy/rubix/lib/L20n';
import { Slider, CircularProgress } from '@material-ui/core';
import { MyLocation } from '@material-ui/icons';
import * as ModalActions from '../../redux/actions/modal.actions';
import { getCurrentLocation } from '../../_config/utils';
import { connect } from 'react-redux';
import MDButton from '../MDButton/MDButton';
import { BLUE } from '../../_config/consts';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const LocationMarker = ({ position, onPositionChange, radius }) => {
  useMapEvents({
    click(e) {
      onPositionChange(e.latlng);
    },
  });

  return position ? (
    <>
      <Marker position={position} />
      {radius && (
        <Circle
          center={position}
          radius={radius}
          pathOptions={{
            color: '#1b97c2',
            fillColor: '#1b97c2',
            fillOpacity: 0.2,
          }}
        />
      )}
    </>
  ) : null;
};
@connect((state) => ({ user: state.user.data }))
class MapSelectorView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      position: null,
      isGettingLocation: false,
      latitude: null,
      longitude: null,
      radiusMeters: 20,
      map: null,
    };
    this._isMounted = false;
    this.initialViewTimeoutId = null;
  }

  isMapUsable(map) {
    if (!map || !this._isMounted) {
      return false;
    }
    try {
      const container = map.getContainer && map.getContainer();
      return !!(container && container.parentNode);
    } catch (error) {
      return false;
    }
  }

  runMapAction(action) {
    const { map } = this.state;
    if (!this.isMapUsable(map)) {
      return;
    }
    try {
      action(map);
    } catch (error) {
      // Map may be torn down while a deferred action runs (e.g. after closing PCU details).
    }
  }

  componentDidMount() {
    this._isMounted = true;
    const { value } = this.props;
    if (value) {
      this.setState({ latitude: value.latitude, longitude: value.longitude, radiusMeters: value.radiusMeters || 20});
      const newPosition = {
        lat: value.latitude,
        lng: value.longitude,
      };
      this.initialViewTimeoutId = setTimeout(() => {
        this.runMapAction((map) => {
          map.setView(newPosition, 18);
        });
      }, 800);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this.initialViewTimeoutId != null) {
      clearTimeout(this.initialViewTimeoutId);
      this.initialViewTimeoutId = null;
    }
  }

  componentDidUpdate(prevProps) {
    const { value } = this.props;
    if (value && value !== prevProps.value) {
      const latitude = value.latitude;
      const longitude = value.longitude;
      const radiusMeters = value.radiusMeters || 20;
      this.setState({ latitude, longitude, radiusMeters });
      if (latitude != null && longitude != null) {
        this.runMapAction((map) => {
          map.setView({ lat: latitude, lng: longitude }, 18);
        });
      }
    }
  }

  handlePositionChange = (newPosition) => {
    this.applyCoordinates(newPosition.lat, newPosition.lng);
  };

  handleRadiusChange = (event, newValue) => {
    const { onChange } = this.props;
    this.setState({ radiusMeters: newValue });
    const newPosition = {
      latitude: this.state.latitude,
      longitude: this.state.longitude,
      radiusMeters: newValue,
    };
    if (onChange) {
      onChange(newPosition);
    }
  };

  applyCoordinates = (latitude, longitude) => {
    const { onChange } = this.props;
    const { radiusMeters, map } = this.state;
    const parsedLatitude = typeof latitude === 'number' ? latitude : parseFloat(String(latitude).replace(',', '.'));
    const parsedLongitude = typeof longitude === 'number' ? longitude : parseFloat(String(longitude).replace(',', '.'));
    this.setState({ latitude: parsedLatitude, longitude: parsedLongitude });
    const hasValidCoords = parsedLatitude != null && parsedLongitude != null
      && Number.isFinite(parsedLatitude) && Number.isFinite(parsedLongitude);
    if (onChange && hasValidCoords) {
      onChange({
        latitude: parsedLatitude,
        longitude: parsedLongitude,
        radiusMeters,
      });
      this.runMapAction((map) => {
        map.setView({ lat: parsedLatitude, lng: parsedLongitude }, 18);
      });
    }
  };

  handleGetCurrentLocation = async () => {
    const { dispatch } = this.props;
    try {
      this.setState({ isGettingLocation: true });
      const newPosition = await getCurrentLocation();
      this.runMapAction((map) => {
        map.setView(newPosition, 18);
      });
      if (this._isMounted) {
        this.handlePositionChange(newPosition);
      }
    } catch (error) {
      dispatch(ModalActions.showModal({
        modalType: 'WARNING_ALERT',
        modalProps: {
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          message: (<h6 className="snack-title"><Entity entity="getCurrentLocationError" /></h6>),
        },
      }));
      console.error(error.message);
    } finally {
      this.setState({ isGettingLocation: false });
    }
  };

  setMap = (map) => {
    this.setState({ map: map });
  };


  render() {
    const { disabled } = this.props;
    const { latitude, longitude, radiusMeters, isGettingLocation } = this.state;
    const defaultCenter = { lat: 45.4642, lng: 9.1900 }; // Milan as default center
    const defaultZoom = 4;
    const validPosition = latitude && longitude ? { lat: latitude, lng: longitude } : defaultCenter;
    return (
      <div>
        <div style={{ 
          height: '400px', 
          width: '100%', 
          marginBottom: '20px',
          position: 'relative'
        }}>
          <div style={{ 
            opacity: isGettingLocation ? 0.5 : 1,
            height: '100%',
            width: '100%',
            position: 'relative'
          }}>
            <MapContainer
              center={validPosition}
              zoom={defaultZoom}
              style={{ height: '100%', width: '100%' }}
              whenCreated={this.setMap}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <LocationMarker
                position={latitude && longitude ? { lat: latitude, lng: longitude } : null}
                onPositionChange={this.handlePositionChange}
                radius={radiusMeters}
              />
            </MapContainer>
          </div>
          {isGettingLocation && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000
            }}>
              <CircularProgress />
            </div>
          )}
        </div>
          <div style={{ padding: '0 20px', marginBottom: '20px' }}>
            <div style={{ marginBottom: '15px', alignItems: 'flex-end' }}>
            <MDButton
              onClick={this.handleGetCurrentLocation}
              backgroundColor={BLUE}
              containerStyle={{ margin: 0, marginTop: 10, width: '70%' }}
              icon={<MyLocation style={{ marginLeft: 10, fontSize: 20 }} />}
              title={<Entity key={isGettingLocation} entity={isGettingLocation ? "gettingLocation" : "getMyLocation"} />}
              disabled={disabled}
            />
            </div>
            {radiusMeters && latitude && longitude ? (
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <h4 style={{ fontWeight: 'bold' }}><Entity entity="circleRadius" />: {radiusMeters}m</h4>
                <Slider
                  value={radiusMeters}
                  onChange={this.handleRadiusChange}
                  min={10}
                  max={500}
                  step={10}
                  valueLabelDisplay="auto"
                  aria-labelledby="circle-radius-slider"
                  disabled={disabled}
                />
              </div>
            ) : null}
        </div>
      </div>
    );
  }
}

export default MapSelectorView; 